import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

interface ResetMFOConfirmationDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => Promise<void>;
}

export function ResetMFOConfirmationDialog({
  isOpen,
  onOpenChange,
  onConfirm,
}: ResetMFOConfirmationDialogProps) {
  const [isResetting, setIsResetting] = useState(false);

  const handleConfirmReset = async () => {
    setIsResetting(true);
    try {
      await onConfirm();
    } finally {
      setIsResetting(false);
      onOpenChange(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-[425px]">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Reset All MFOs
          </AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to reset all MFOs?
            <div className="mt-2 p-3 bg-muted rounded-md">
              <p className="font-medium">Warning: This action is irreversible</p>
              <p className="text-sm text-muted-foreground mt-1">
                This will permanently delete all MFOs and their associated data. This action cannot be undone.
                Please make sure you have backed up any important data before proceeding.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isResetting}>Cancel</AlertDialogCancel>
          <Button 
            variant="destructive" 
            onClick={handleConfirmReset}
            disabled={isResetting}
            className="gap-2"
          >
            {isResetting ? "Resetting..." : "Reset All MFOs"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
} 